import React from 'react';
import { AlertCircle, XCircle, CheckCircle2, Droplets } from 'lucide-react';
export function ContentSections() {
  return <div className="bg-white" data-id="element-1">
      {/* Opening Hook */}
      <section className="py-16 md:py-24 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2">
        <h2 className="text-3xl md:text-4xl font-bold mb-8 leading-tight" data-id="element-3">
          ¿Alguna vez has dudado antes de agacharte para jugar con tus nietos?
        </h2>
        <p className="text-xl text-text-dark leading-relaxed mb-6" data-id="element-4">
          O tal vez has sentido ese{' '}
          <span className="italic" data-id="element-5">"suspiro de resignación"</span> al levantarte
          de la silla por la mañana. No estás sola. La rigidez no tiene por qué
          ser tu nueva normalidad.
        </p>
        <p className="text-lg text-text-muted leading-relaxed" data-id="element-6">
          Existe una forma de "aceitar" tus articulaciones y recuperar la
          confianza en tu cuerpo, sin necesidad de gimnasios costosos,
          ejercicios complicados o medicamentos milagrosos.
        </p>
      </section>

      {/* The Problem */}
      <section className="py-16 bg-sage-light/30" data-id="element-7">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-card relative overflow-hidden" data-id="element-9">
            <div className="absolute top-0 right-0 w-32 h-32 bg-sage-light rounded-bl-full opacity-50" data-id="element-10"></div>

            <div className="flex items-center gap-4 mb-6" data-id="element-11">
              <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center text-sage" data-id="element-12">
                <AlertCircle size={24} data-id="element-13" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" data-id="element-14">
                El ciclo vicioso de la rigidez
              </h2>
            </div>

            <div className="space-y-6 text-lg text-text-muted leading-relaxed" data-id="element-15">
              <p data-id="element-16">
                Con el paso de los años, nuestro cuerpo tiende a "bloquear"
                rangos de movimiento que no usamos, creando esa sensación de
                bisagras oxidadas en las mañanas.
              </p>
              <p data-id="element-17">
                El dolor lumbar, la tensión en el cuello y la falta de
                equilibrio no son solo "señales de la edad", son señales de que
                tus articulaciones han dejado de recibir la hidratación y el
                movimiento que necesitan.
              </p>
              <p className="font-medium text-sage-dark" data-id="element-18">
                El miedo a caerte o a lesionarte te hace moverte menos, y
                moverte menos te hace sentir más rígida. Es un ciclo vicioso que
                limita tu independencia y te roba momentos de alegría.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why what you tried didn't work */}
      <section className="py-16 md:py-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-19">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-20">
          Por qué lo que has intentado no funciona
        </h2>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-21">
          {[{
          title: 'Gimnasios Genéricos',
          desc: 'Las rutinas son demasiado rápidas y no respetan la estructura articular después de los 55.'
        }, {
          title: 'Solo Caminar',
          desc: 'Es excelente, pero no libera la tensión acumulada en las caderas y la columna vertebral.'
        }, {
          title: 'Cremas y Pastillas',
          desc: "Solo 'apagan la alarma' del dolor, pero no reparan la causa: la falta de movilidad real."
        }].map((item, idx) => <div key={idx} className="bg-cream rounded-2xl p-6 border border-sage-light/50" data-id="element-22">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-red-400 mb-4 shadow-sm" data-id="element-23">
                <XCircle size={20} data-id="element-24" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-25">{item.title}</h3>
              <p className="text-text-muted" data-id="element-26">{item.desc}</p>
            </div>)}
        </div>
      </section>

      {/* The Unique Mechanism */}
      <section className="py-16 md:py-24 bg-sage text-white relative overflow-hidden" data-id="element-27">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" data-id="element-28"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-29">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-8 backdrop-blur-sm" data-id="element-30">
            <Droplets size={32} className="text-cream" data-id="element-31" />
          </div>

          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-32">
            El Secreto: La "Lubricación Biológica"
          </h2>

          <p className="text-xl leading-relaxed text-sage-light mb-8" data-id="element-33">
            Presentamos el{' '}
            <strong className="text-white font-bold" data-id="element-34">
              Método Movilidad 360°
            </strong>
            . A diferencia del ejercicio tradicional, este sistema se basa en
            movimientos circulares y de apertura diseñados para enviar señales
            de seguridad a tu sistema nervioso.
          </p>

          <div className="bg-sage-dark/30 rounded-2xl p-6 md:p-8 backdrop-blur-sm border border-white/10" data-id="element-35">
            <p className="text-lg md:text-xl font-heading italic" data-id="element-36">
              "Permite que tus articulaciones liberen líquido sinovial (tu
              aceite natural) de forma inmediata, devolviéndote la fluidez que
              creías perdida."
            </p>
          </div>
        </div>
      </section>
    </div>;
}